// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from '@/integrations/supabase/client';
import { Users, Target, TrendingUp, Calendar, Trophy, Phone } from 'lucide-react';
import { getSessionUser } from '@/lib/authSession';

interface TeamStats {
  totalProspects: number;
  thisMonthRecruits: number;
  activeParticipants: number;
  upcomingRegistrations: number;
  renewalOpportunities: number;
  stageBreakdown: { [key: string]: number };
}

const TeamStatsOverview: React.FC = () => {
  const [stats, setStats] = useState<TeamStats>({
    totalProspects: 0,
    thisMonthRecruits: 0,
    activeParticipants: 0,
    upcomingRegistrations: 0,
    renewalOpportunities: 0,
    stageBreakdown: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Get current user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', (await getSessionUser()).data.user?.id)
        .single();

      if (!profile) return;

      // Fetch team prospects data
      const { data: prospects, error } = await supabase
        .from('team_prospects')
        .select('*')
        .eq('sales_rep_id', profile.id);

      if (error) throw error;

      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // Calculate stats
      const totalProspects = prospects?.length || 0;
      const thisMonthRecruits = prospects?.filter(p => 
        new Date(p.created_at) >= thisMonth
      ).length || 0;
      
      const activeParticipants = prospects?.filter(p => 
        p.pipeline_stage === 'active_participant'
      ).length || 0;
      
      const upcomingRegistrations = prospects?.filter(p => 
        ['interest_shown', 'registration_started'].includes(p.pipeline_stage)
      ).length || 0;
      
      const renewalOpportunities = prospects?.filter(p => 
        p.pipeline_stage === 'renewal'
      ).length || 0;

      // Stage breakdown
      const stageBreakdown = prospects?.reduce((acc, prospect) => {
        acc[prospect.pipeline_stage] = (acc[prospect.pipeline_stage] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number }) || {};

      setStats({
        totalProspects,
        thisMonthRecruits,
        activeParticipants,
        upcomingRegistrations,
        renewalOpportunities,
        stageBreakdown
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Team Prospects",
      value: stats.totalProspects,
      icon: Users,
      description: "Teams in pipeline",
      color: "text-blue-600"
    },
    {
      title: "Teams Recruited This Month",
      value: stats.thisMonthRecruits,
      icon: Target,
      description: "New prospects added",
      color: "text-green-600"
    },
    {
      title: "Active Participating Teams",
      value: stats.activeParticipants,
      icon: Trophy,
      description: "Currently registered",
      color: "text-purple-600"
    },
    {
      title: "Upcoming Registrations",
      value: stats.upcomingRegistrations,
      icon: Calendar,
      description: "Ready to register",
      color: "text-orange-600"
    },
    {
      title: "Renewal Opportunities",
      value: stats.renewalOpportunities,
      icon: TrendingUp,
      description: "Teams to renew",
      color: "text-pink-600"
    }
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-16 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {statCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold">
                      {stat.value}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {stat.description}
                    </p>
                  </div>
                  <IconComponent className={`h-8 w-8 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pipeline Stage Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Pipeline Stage Breakdown</CardTitle>
          <CardDescription>
            Distribution of teams across recruitment stages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {Object.entries(stats.stageBreakdown).map(([stage, count]) => (
              <div key={stage} className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {count}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Phone className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="font-semibold">Follow-up Calls</h3>
                <p className="text-sm text-muted-foreground">
                  Schedule outreach activities
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Target className="h-8 w-8 text-green-600" />
              <div>
                <h3 className="font-semibold">New Campaign</h3>
                <p className="text-sm text-muted-foreground">
                  Start recruitment campaign
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-purple-600" />
              <div>
                <h3 className="font-semibold">Event Planning</h3>
                <p className="text-sm text-muted-foreground">
                  Plan team events
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export { TeamStatsOverview };